import React, { useState, useEffect } from 'react';
import { Button, App, Input, Table, Checkbox, Pagination } from 'antd';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import styles from './LiveStreamingProduct.module.scss';
import img1 from '@/assets/images/216a7435bae7bcf5774887274d7c6ee@2x.png';
import { useTranslation } from 'react-i18next';


const { Search } = Input;

interface ProductItem {
  id: string;
  name: string;
  price: string;
  inventory: number;
  creationTime: string;
  image: string;
  description: string;
}

/**
 * LiveStreamingProduct 组件 - 直播产品列表管理页面
 * 显示产品列表，支持搜索、导入、批量删除等功能
 */
const LiveStreamingProduct: React.FC = () => {
  // 使用 Redux dispatch 和 Antd message hook
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const isMobile = useAppSelector((state) => state.searchBar.isMobile);
  //国际化语言切换
  const { t, i18n } = useTranslation();
  // 状态管理
  const [searchValue, setSearchValue] = useState(t('LiveStreamingProduct.TableLamp'));
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 模拟产品数据
  const productData: ProductItem[] = Array.from({ length: 30 }, (_, index) => ({
    id: `product_${index + 1}`,
    name: 'Handmade Bamboo Basket: Woven with Bamboo Strips, Fine, Durable',
    price: '$5.99',
    inventory: 0,
    creationTime: '07/03/2025 14:00:00',
    image: img1,
    description: 'Shape Classification: Square Handheld Wicker Basket'
  }));

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRowKeys(productData.map(item => item.id));
    } else {
      setSelectedRowKeys([]);
    }
  };

  // 单个选择
  const handleSelectItem = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedRowKeys([...selectedRowKeys, id]);
    } else {
      setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
    }
  };

  // 搜索处理
  const handleSearch = (value: string) => {
    console.log('Search:', value);
    // 这里实现搜索逻辑
  };

  // 导入处理
  const handleImport = () => {
    message.info('Import functionality');
  };

  // 批量删除处理
  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select items to delete');
      return;
    }
    message.info(`Delete ${selectedRowKeys.length} items`);
  };

  // 编辑处理
  const handleEdit = (id: string) => {
    message.info(`Edit product: ${id}`);
  };

  // 删除处理
  const handleDelete = (id: string) => {
    message.info(`Delete product: ${id}`);
  };

  //mounted 和unmounted
  useEffect(() => {

  }, []);

  // 渲染产品行
  const renderProductRow = (product: ProductItem) => (
    <div key={product.id} className={styles['product-row']}>
      <div className={styles['product-checkbox']}>
        <Checkbox
          checked={selectedRowKeys.includes(product.id)}
          onChange={(e) => handleSelectItem(product.id, e.target.checked)}
        />
      </div>
      <div className={styles['product-content']}>
        <div className={styles['product-main']}>
          <img src={product.image} alt={product.name} className={styles['product-image']} />
          <div className={styles['product-info']}>
            <div className={styles['product-name']}>{product.name}</div>
            <div className={styles['product-description']}>{product.description}</div>
          </div>
        </div>
        <div className={styles['product-details']}>
          <div className={styles['product-detail-item']}>
            <span className={styles['detail-label']}>Price:</span>
            <span className={styles['detail-value']}>{product.price}</span>
          </div>
          <div className={styles['product-detail-item']}>
            <span className={styles['detail-label']}>Inventory:</span>
            <span className={styles['detail-value']}>{product.inventory}</span>
          </div>
          <div className={styles['product-detail-item']}>
            <span className={styles['detail-label']}>Created:</span>
            <span className={styles['detail-value']}>{product.creationTime}</span>
          </div>
        </div>
        <div className={styles['product-actions']}>
          <Button 
            type="link" 
            className={styles['action-button']}
            onClick={() => handleEdit(product.id)}
          >
            Edit
          </Button>
          <Button 
            type="link" 
            className={styles['action-button']} 
            danger
            onClick={() => handleDelete(product.id)}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles['live-streaming-product-container']}>
      {/* 顶部操作栏 */}
      <div className={styles['header-section']}>
        <div className={styles['search-section']}>
          <Search
            placeholder={t('LiveStreamingProduct.TableLamp')}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            className={styles['search-input']}
            size="large"
          />
        </div>
        
        <div className={styles['action-section']}>
          <Button 
            type="primary" 
            className={styles['import-button']}
            onClick={handleImport}
          >
            {t('common.Import')}
          </Button>
          <Button 
            className={styles['bulk-delete-button']}
            onClick={handleBulkDelete}
            disabled={selectedRowKeys.length === 0}
          >
            {t('common.BulkDelete')}
          </Button>
          <span className={styles['total-count']}>{t('common.Total')} {productData.length} {t('common.items')}</span>
        </div>
      </div>

      {/* 产品列表 */}
      <div className={styles['product-list-section']}>

        {!isMobile ? (
          // 桌面端表格
          <div className={styles['desktop-table']}>
            <div className={styles['table-header']}>
              <div className={styles['header-checkbox']}>
                <Checkbox
                  checked={selectedRowKeys.length === productData.length && productData.length > 0}
                  indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < productData.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </div>
              <div className={styles['header-product']}>{t('storeInformation.ProductName')}</div>
              <div className={styles['header-price']}>{t('storeInformation.Price')}</div>
              <div className={styles['header-inventory']}>{t('storeInformation.Inventory')}</div>
              <div className={styles['header-time']}>{t('storeInformation.CreationTime')}</div>
              <div className={styles['header-operations']}>{t('storeInformation.Operations')}</div>
            </div>
            
            <div className={styles['table-body']}>
              {productData.map((product) => (
                <div key={product.id} className={styles['table-row']}>
                  <div className={styles['row-checkbox']}>
                    <Checkbox
                      checked={selectedRowKeys.includes(product.id)}
                      onChange={(e) => handleSelectItem(product.id, e.target.checked)}
                    />
                  </div>
                  <div className={styles['row-product']}>
                    <img src={product.image} alt={product.name} className={styles['product-image']} />
                    <div className={styles['product-info']}>
                      <div className={styles['product-name']}>{product.name}</div>
                      <div className={styles['product-description']}>{product.description}</div>
                    </div>
                  </div>
                  <div className={styles['row-price']}>{product.price}</div>
                  <div className={styles['row-inventory']}>{product.inventory}</div>
                  <div className={styles['row-time']}>{product.creationTime}</div>
                  <div className={styles['row-operations']}>
                    <Button 
                      type="link" 
                      className={styles['edit-button']}
                      onClick={() => handleEdit(product.id)}
                    >
                      {t('storeInformation.Edit')}
                    </Button>
                    <Button 
                      type="link" 
                      className={styles['delete-button']} 
                      danger
                      onClick={() => handleDelete(product.id)}
                    >
                      {t('storeInformation.Delete')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // 移动端卡片列表
          <div className={styles['mobile-list']}>
            <div className={styles['mobile-header']}>
              <Checkbox
                checked={selectedRowKeys.length === productData.length && productData.length > 0}
                indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < productData.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <span className={styles['select-all-text']}>{t('common.SelectAll')}</span>
            </div>
            
            <div className={styles['mobile-products']}>
              {productData.map(renderProductRow)}
            </div>
          </div>
        )}
      </div>

      {/* 分页 */}
      <div className={styles['pagination-section']}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={productData.length}
          showSizeChanger={false}
          showQuickJumper={!isMobile}
          onChange={(page) => setCurrentPage(page)}
          className={styles['pagination']}
        />
      </div>
    </div>
  );
};

export default LiveStreamingProduct;

